import React from 'react';
import { motion } from 'framer-motion';
import { ArrowDown, ArrowUpRight, Play, Maximize2, Compass } from 'lucide-react';
import { studioData, portfolioProjects } from '../data/portfolioData';

interface HeroProps {
  onSelectProject?: (id: string) => void;
}

export const Hero: React.FC<HeroProps> = ({ onSelectProject }) => {
  const featured = portfolioProjects[0];

  return (
    <section className="relative min-h-screen flex flex-col justify-between pt-28 pb-12 px-6 md:px-12 overflow-hidden">
      {/* Top Technical Metadata Bar */}
      <div className="max-w-7xl mx-auto w-full flex flex-wrap items-center justify-between gap-4 py-3 border-b border-white/10 text-[10px] font-mono uppercase tracking-widest text-arch-muted z-10">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-arch-accent" />
          <span>{studioData.coordinates}</span>
        </div>
        <div className="hidden sm:flex items-center gap-6">
          <span>PORTFOLIO MONOGRAPH VOL. IV</span>
          <span>TYPOLOGY: RESIDENTIAL & COMMERCIAL</span>
        </div>
        <div className="flex items-center gap-2 text-neutral-400">
          <Compass className="w-3 h-3 text-arch-accent" />
          <span>ORIENTATION: S-SW 198°</span>
        </div>
      </div>

      {/* Main Architectural Hero Content */}
      <div className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-12 items-center my-auto py-8 z-10">
        {/* Left Column: Editorial Headline & Statement */}
        <div className="lg:col-span-7 space-y-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="space-y-4"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded bg-white/5 border border-white/10 text-xs font-mono text-arch-accent tracking-widest">
              <span>CONTEMPORARY ARCHITECTURAL PRACTICE</span>
            </div>

            <h1 className="text-4xl sm:text-6xl md:text-7xl font-serif tracking-tight leading-[1.08] text-white">
              Tectonic <span className="italic font-normal text-arch-limestone">Clarity</span>.
              <br />
              Human <span className="italic font-normal text-arch-accent">Scale</span>.
            </h1>

            <p className="max-w-xl text-neutral-300 text-sm sm:text-base leading-relaxed font-light">
              {studioData.philosophySummary}
            </p>
          </motion.div>

          {/* Action CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-wrap items-center gap-4 pt-2"
          >
            <a
              href="#works"
              className="inline-flex items-center gap-3 px-6 py-3.5 bg-arch-accent text-[#0A0B0D] text-xs font-mono uppercase tracking-widest font-semibold hover:bg-white transition-colors duration-300 rounded-sm group"
            >
              <span>Explore Selected Works</span>
              <ArrowDown className="w-3.5 h-3.5 transition-transform group-hover:translate-y-0.5" />
            </a>

            <a
              href="#inquire"
              className="inline-flex items-center gap-2 px-6 py-3.5 bg-white/5 hover:bg-white/10 border border-white/15 text-white text-xs font-mono uppercase tracking-widest transition-colors duration-300 rounded-sm group"
            >
              <span>Consultation Inquiry</span>
              <ArrowUpRight className="w-3.5 h-3.5 text-arch-accent transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </motion.div>

          {/* Studio Metrics Counter */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="grid grid-cols-2 sm:grid-cols-4 gap-6 pt-6 border-t border-white/10"
          >
            {studioData.stats.map((stat) => (
              <div key={stat.label} className="space-y-1">
                <div className="text-2xl sm:text-3xl font-serif font-semibold text-white tracking-tight">
                  {stat.value}
                </div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-arch-muted">
                  {stat.label}
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Right Column: Featured Architectural Showcase Frame */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5 relative"
        >
          <div className="relative group cursor-pointer" onClick={() => onSelectProject && onSelectProject(featured.id)}>
            {/* Technical Drafting Frame Corner Accents */}
            <div className="absolute -top-2 -left-2 w-4 h-4 border-t-2 border-l-2 border-arch-accent z-20" />
            <div className="absolute -top-2 -right-2 w-4 h-4 border-t-2 border-r-2 border-arch-accent z-20" />
            <div className="absolute -bottom-2 -left-2 w-4 h-4 border-b-2 border-l-2 border-arch-accent z-20" />
            <div className="absolute -bottom-2 -right-2 w-4 h-4 border-b-2 border-r-2 border-arch-accent z-20" />

            {/* Featured Image Container */}
            <div className="relative aspect-[4/5] rounded-sm overflow-hidden border border-white/15 bg-arch-card">
              <img
                src={featured.heroImage}
                alt={featured.title}
                className="w-full h-full object-cover grayscale-[25%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0D] via-transparent to-transparent opacity-80 group-hover:opacity-60 transition-opacity" />

              {/* Top Tag */}
              <div className="absolute top-4 left-4 z-10 flex items-center gap-2">
                <span className="px-2.5 py-1 rounded bg-[#0A0B0D]/80 backdrop-blur border border-white/10 text-[10px] font-mono text-arch-accent tracking-wider">
                  FEATURED WORK // {featured.year}
                </span>
              </div>

              {/* View Overlay Button */}
              <div className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-[#0A0B0D]/80 backdrop-blur border border-white/20 flex items-center justify-center text-white group-hover:bg-arch-accent group-hover:text-[#0A0B0D] transition-colors">
                <Maximize2 className="w-4 h-4" />
              </div>

              {/* Bottom Project Details */}
              <div className="absolute bottom-0 left-0 right-0 p-6 z-10 space-y-2">
                <div className="text-[11px] font-mono text-arch-muted uppercase tracking-wider">
                  {featured.category} • {featured.location}
                </div>
                <h2 className="text-xl font-serif text-white group-hover:text-arch-accent transition-colors font-semibold">
                  {featured.title}
                </h2>
                <div className="flex items-center justify-between pt-2 border-t border-white/10 text-xs font-mono text-neutral-300">
                  <span>Footprint: {featured.area}</span>
                  <span className="text-arch-accent flex items-center gap-1">
                    Inspect Blueprint & Specs <ArrowUpRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </div>
            </div>

            {/* Subtle Blueprint Drawing Tag */}
            <div className="mt-3 flex items-center justify-between text-[10px] font-mono text-arch-muted">
              <span>CAD REF: PL-01-SEC-A</span>
              <span className="flex items-center gap-1.5 text-neutral-400">
                <Play className="w-2.5 h-2.5 fill-current text-arch-accent" /> Click to view full project breakdown
              </span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Bottom Scroll Indicator Bar */}
      <div className="max-w-7xl mx-auto w-full flex items-center justify-between pt-6 border-t border-white/10 text-[10px] font-mono text-arch-muted uppercase tracking-wider z-10">
        <div className="flex items-center gap-4">
          <span className="text-white/40">|---|---|---|---|</span>
          <span>SCALE: 1:500 ARCHITECTURAL STUDY</span>
        </div>
        <a href="#works" className="flex items-center gap-2 hover:text-arch-accent transition-colors">
          <span>SCROLL DOWN TO DISCOVER</span>
          <ArrowDown className="w-3 h-3 animate-bounce" />
        </a>
      </div>
    </section>
  );
};
